import i18n from '@/utils/i18n';
import React, { useEffect, useMemo, useState } from 'react';
import { ActionSheetContent } from '@/components/action-sheet';
import { ActivityIndicator } from '@/components/activity-indicator';
import { createStyles } from '@/contexts/theme-context';
import { FONTS, TYPOGRAPHY } from '@/styles';
import { PrintTagTemplateFieldsFragment } from '@/types';
import {
  TagPrintingTemplateCarousel,
  TemplateListOption,
} from '@/components/tag-printing-template-carousel';
import { Text, View } from 'react-native';
import { usePrintTagTemplates } from '@/hooks/use-print-tag-templates';

interface Props {
  /**
   * Suppresses the "you can change this later" subtitle for callers that
   * offer no way to change the template afterwards.
   */
  hideChangeLaterMessage?: boolean;
  initialTemplate: PrintTagTemplateFieldsFragment | null;
  onSelectTemplate: (
    template: PrintTagTemplateFieldsFragment | null,
  ) => void | Promise<void>;
  /**
   * Restricts the list to templates the app can render on device. Custom tags
   * are rendered locally from user input, so templates without a text
   * template cannot be used for them.
   */
  requireLocalRendering?: boolean;
}

export const SelectTagPrintBatchTemplate: React.FC<Props> = ({
  onSelectTemplate,
  hideChangeLaterMessage = false,
  initialTemplate,
  requireLocalRendering = false,
}) => {
  const [selectedTemplate, setSelectedTemplate] =
    useState<PrintTagTemplateFieldsFragment | null>(initialTemplate);
  const [styles] = useStyles();

  const { loading, templates } = usePrintTagTemplates({
    requireLocalRendering,
  });

  useEffect(() => {
    if (
      templates.length === 1 &&
      (!selectedTemplate || selectedTemplate.uuid !== templates[0].uuid)
    ) {
      setSelectedTemplate({
        metadata: templates[0].metadata,
        uuid: templates[0].uuid,
      });
    }
  }, [templates, selectedTemplate]);

  const templateOptions: TemplateListOption[] = useMemo(
    () =>
      templates.map(
        ({ metadata, preview_image_url_list, display_name, name, uuid }) => ({
          metadata: {
            metadata,
            previewImageUrlList: preview_image_url_list,
          },
          title: display_name ?? name,
          value: uuid,
        }),
      ),
    [templates],
  );

  return (
    <ActionSheetContent
      actions={[i18n.t('continue')]}
      disabledIndices={selectedTemplate ? [] : [0]}
      message={
        <>
          {hideChangeLaterMessage ? null : (
            <Text style={[TYPOGRAPHY.p, styles.message]}>
              {i18n.t('you_can_change_this_later')}
            </Text>
          )}
          {loading ? (
            <ActivityIndicator />
          ) : (
            <View style={styles.listContainer}>
              <TagPrintingTemplateCarousel
                onTemplateSelect={({ metadata, value }) =>
                  setSelectedTemplate({
                    metadata: metadata?.metadata,
                    uuid: value as string,
                  })
                }
                selectedTemplateUUID={
                  selectedTemplate ? selectedTemplate.uuid : null
                }
                templateOptions={templateOptions}
              />
            </View>
          )}
        </>
      }
      onAction={() => onSelectTemplate(selectedTemplate)}
      title={i18n.t('select_a_template')}
      titleStyle={[FONTS.primarySemiBold, styles.title]}
    />
  );
};

const useStyles = createStyles((theme) => ({
  listContainer: {
    marginVertical: 24,
  },
  message: {
    color: theme.sk.color.text.secondary,
  },
  title: {
    fontSize: 22,
    marginBottom: 8,
    textAlign: 'left',
  },
}));
